import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Eye, Calendar, Clock, Film, Activity } from 'lucide-react';

interface SwingAnalysis {
  id: string;
  user_id: string;
  video_id: string | null;
  playback_id: string;
  fps: number;
  frame_count: number;
  duration_s: number | null;
  created_at: string;
}

interface AnalysisListViewProps {
  analyses: SwingAnalysis[];
}

export function AnalysisListView({ analyses }: AnalysisListViewProps) {
  const navigate = useNavigate();

  if (analyses.length === 0) {
    return (
      <Card>
        <CardContent className="p-8">
          <div className="text-center">
            <Activity className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">
              No Pose Analyses Yet
            </h3>
            <p className="text-muted-foreground mb-4">
              Start by capturing pose data from the Test Pose page
            </p>
            <Button onClick={() => navigate('/test-pose')}>
              Go to Test Pose
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {analyses.map((analysis) => (
        <Card key={analysis.id} className="hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <Film className="w-5 h-5 text-primary" />
                  <h3 className="font-medium text-foreground">
                    Analysis {analysis.id.slice(0, 8)}...
                  </h3>
                  <Badge variant="secondary">
                    {analysis.frame_count || 0} frames
                  </Badge>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">
                      {new Date(analysis.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">
                      {analysis.duration_s ? `${analysis.duration_s.toFixed(1)}s` : 'N/A'}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Activity className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">
                      {analysis.fps} FPS
                    </span>
                  </div>
                  
                  {analysis.video_id && (
                    <div className="text-muted-foreground">
                      Linked to video
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => navigate(`/pose-analysis/${analysis.id}`)}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  View Details
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}